import { SINIA_ICON } from "../../assets";
import {
  NEWS_ONE,
  STATISTICS,
  resourceDetailPath,
} from "../../constants/routes";
import {
  PublicNodeDTO,
  PublicNoticiaDTO,
  PublicNoticiaDetailDTO,
  PublicOrganizationDTO,
  PublicResourceDTO,
  PublicResourceDetailDTO,
  ResourceFileDTO,
  ResourceVisualizationDTO,
} from "../../types/api/cmsPublic";
import {
  MediaFileVM,
  NodeCardVM,
  NoticiaCategoryVM,
  NoticiaDetailVM,
  NoticiaListItemVM,
  OrganizationNodesVM,
  ResourceDetailVM,
  ResourceDetailVisualizationVM,
  ResourceListItemVM,
} from "../../types/viewModels/cmsPublic";
import { formatDate } from "../../utilities/formatDate";
import { toVideoEmbedUrl } from "../../utilities/videoEmbed";

const iconFallback = String(SINIA_ICON);

const safeText = (value?: string | null) => (value || "").trim();

const toVisualization = (
  visualization: ResourceVisualizationDTO
): ResourceDetailVisualizationVM => ({
  provider: safeText(visualization.provider) || "junar",
  datasetId: safeText(visualization.datasetId),
  externalUrl: safeText(visualization.externalUrl) || undefined,
  embedUrl: safeText(visualization.embedUrl) || undefined,
});

export const mapNodeToCardVM = (node: PublicNodeDTO): NodeCardVM => ({
  id: node.id,
  slug: node.slug,
  title: safeText(node.name),
  description: safeText(node.subtitulo) || safeText(node.descripcion),
  iconSrc: safeText(node.icono) || iconFallback,
  href: `${STATISTICS}?node=${encodeURIComponent(node.slug)}`,
});

export const mapOrganizationToVM = (
  organization: PublicOrganizationDTO
): OrganizationNodesVM => ({
  id: organization.id,
  slug: organization.slug,
  name: safeText(organization.name),
  subtitle: safeText(organization.subtitulo),
  description: safeText(organization.descripcion),
  iconSrc: safeText(organization.icono) || iconFallback,
  nodes: (organization.nodes || []).map(mapNodeToCardVM),
});

export const mapResourceToListItemVM = (
  resource: PublicResourceDTO
): ResourceListItemVM => ({
  id: resource.id,
  slug: resource.slug,
  title: safeText(resource.title),
  description: safeText(resource.description),
  iconSrc: iconFallback,
  href: resourceDetailPath(resource.slug),
  nodeName: safeText(resource.node?.name),
  nodeSlug: safeText(resource.node?.slug),
  publishedAt: safeText(resource.publishedAt),
  tags: (resource.etiquetas || [])
    .map((tag) => safeText(tag.name))
    .filter(Boolean),
});

export const mapResourceDetailToVM = (
  resource: PublicResourceDetailDTO
): ResourceDetailVM => ({
  id: resource.id,
  slug: resource.slug,
  title: safeText(resource.title),
  description: safeText(resource.description),
  nodeName: safeText(resource.node?.name),
  nodeSlug: safeText(resource.node?.slug),
  publishedAt: safeText(resource.publishedAt),
  marcoNormativoName: safeText(resource.marcoNormativo?.name),
  marcoNormativoUrl: safeText(resource.marcoNormativo?.url),
  tags: (resource.etiquetas || [])
    .map((tag) => safeText(tag.name))
    .filter(Boolean),
  visualizations: (resource.visualizations || [])
    .map(toVisualization)
    .filter((visualization) =>
      Boolean(
        visualization.datasetId ||
          visualization.externalUrl ||
          visualization.embedUrl
      )
    ),
});

export const mapMediaFile = (
  file?: ResourceFileDTO | null
): MediaFileVM | null => {
  const url = safeText(file?.url);
  if (!url) {
    return null;
  }

  return {
    name: safeText(file?.name) || url,
    url,
  };
};

const mapMediaFiles = (files?: ResourceFileDTO[] | null): MediaFileVM[] =>
  (files || [])
    .map(mapMediaFile)
    .filter((file): file is MediaFileVM => file !== null);

const mapCategories = (
  categorias?: PublicNoticiaDTO["categorias"]
): NoticiaCategoryVM[] =>
  (categorias || [])
    .map((categoria) => ({
      slug: safeText(categoria?.slug),
      nombre: safeText(categoria?.nombre),
    }))
    .filter((categoria) => Boolean(categoria.slug));

export const mapNoticiaToListItemVM = (
  noticia: PublicNoticiaDTO
): NoticiaListItemVM => {
  const publishedAt = safeText(noticia.publishedAt);

  return {
    id: noticia.id,
    slug: noticia.slug,
    title: safeText(noticia.titulo),
    summary: safeText(noticia.resumen),
    imageUrl: mapMediaFile(noticia.imagenDestacada)?.url || "",
    publishedAt,
    publishedAtLabel: formatDate(publishedAt),
    href: NEWS_ONE.replace(":id", encodeURIComponent(noticia.slug)),
    categories: mapCategories(noticia.categorias),
  };
};

export const mapNoticiaDetailToVM = (
  noticia: PublicNoticiaDetailDTO
): NoticiaDetailVM => {
  const videoOriginalUrl = safeText(noticia.videoUrl);

  return {
    ...mapNoticiaToListItemVM(noticia),
    content: safeText(noticia.contenido),
    gallery: mapMediaFiles(noticia.galeria),
    videoEmbedUrl: toVideoEmbedUrl(videoOriginalUrl),
    videoOriginalUrl,
    videoFile: mapMediaFile(noticia.videoArchivo),
    attachments: mapMediaFiles(noticia.adjuntos),
  };
};
